/**
 * Flächenerkennung ("Zauberstab") für den Marker-Editor.
 *
 * Klick auf eine Fläche im Hallenplan → zusammenhängende, farblich ähnliche
 * Region wird erkannt und als Polygon-Kontur (in %-Koordinaten 0–100)
 * zurückgegeben. Pipeline:
 *
 *   1. Plan in ein Offscreen-Canvas rastern (feste Analysebreite).
 *   2. Versuch A ("Malbuch"): Floodfill, der nur an deutlich dunkleren
 *      Pixeln (Konturlinien) stoppt. Deckt den Normalfall technischer
 *      Pläne ab: helle Flächen mit dunkler Umrandung — auch wenn Fläche
 *      und Umgebung dieselbe Farbe haben (weiß auf weiß).
 *   3. Versuch B (Fallback): Ähnlichkeitsmaske zur Klickfarbe mit
 *      morphologischem Schließen — überbrückt dünne Musterlinien
 *      (Schraffuren, Förderband-Streifen), an denen Versuch A die Fläche
 *      in Mini-Zellen zerhackt. Bewusst enge Farbtoleranz, sonst laufen
 *      benachbarte Weißtöne ineinander.
 *   4. Konturverfolgung (Kantenlauf mit Rechte-Hand-Regel) + Vereinfachung
 *      (Ramer–Douglas–Peucker).
 *
 * Der Kern (`detectRegion`) arbeitet auf rohen RGBA-Daten und ist bewusst
 * DOM-frei, damit er ohne Browser testbar bleibt.
 */

import { t } from "./i18n";

export type WandFailure = "too-large" | "too-small" | "no-region";

export type WandResult =
  | { ok: true; points: [number, number][]; areaPct: number }
  | { ok: false; reason: WandFailure };

/** Analysebreite in px — Kompromiss aus Präzision und Rechenzeit. */
const ANALYSIS_WIDTH = 1200;
/** Luminanz-Abstand zur Klickfarbe, ab dem ein Pixel als Konturlinie gilt. */
const LUMA_BARRIER_DELTA = 60;
/** Max. RGB-Abstand (euklidisch) zur Klickfarbe, um als "gleiche Fläche" zu gelten. */
const COLOR_TOLERANCE = 25;
/** Dilations-/Erosions-Durchgänge beim Schließen (überbrückt Linien bis ~2·r px). */
const CLOSE_RADIUS = 2;
/** Region größer als dieser Anteil des Plans → vermutlich Hallenboden getroffen. */
const MAX_AREA_FRAC = 0.4;
/** Region kleiner als dieser Anteil → Fehlklick/Rauschen. */
const MIN_AREA_FRAC = 0.0003;
/** Obergrenze Eckpunkte nach Vereinfachung. */
const MAX_POINTS = 60;

// ─────────────────────────────────────────────────────────────────────────────
// Canvas-Seite (Browser)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sorgt dafür, dass das öffnende `<svg>`-Tag eine explizite Pixel-Größe
 * hat. SVGs mit nur `viewBox` (oder `width="100%"`) haben keine
 * intrinsische Größe — ohne die rastert `drawImage` in Safari zu 0×0 oder
 * gar nicht. Bewusst per String-Ersetzung statt DOMParser/XMLSerializer:
 * der Round-Trip über die DOM-Serialisierung produziert bei manchen
 * Export-SVGs (Inkscape/Visio, XML-Deklaration, Namespaces) Bytes, die
 * `img.decode()` in WebKit ablehnt — genau der "cannot be decoded"-Fehler.
 *
 * Rein (kein DOM) → in Node testbar.
 */
export function ensureSvgPixelSize(text: string): string {
  const open = text.match(/<svg\b[^>]*>/i);
  if (!open) return text;
  const tag = open[0];

  const hasPxWidth = /\bwidth\s*=\s*["']?\s*[\d.]+\s*(px)?\s*["']?/i.test(tag) &&
    !/\bwidth\s*=\s*["']?[\d.]+%/i.test(tag);
  const hasPxHeight = /\bheight\s*=\s*["']?\s*[\d.]+\s*(px)?\s*["']?/i.test(tag) &&
    !/\bheight\s*=\s*["']?[\d.]+%/i.test(tag);
  if (hasPxWidth && hasPxHeight) return text;

  const vb = tag.match(
    /viewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/i,
  );
  const w = vb ? parseFloat(vb[1]) : 1600;
  const h = vb ? parseFloat(vb[2]) : 900;
  if (!(w > 0 && h > 0)) return text;

  // Vorhandene (evtl. prozentuale) width/height entfernen, dann Pixel setzen.
  const newTag = tag
    .replace(/\swidth\s*=\s*["'][^"']*["']/i, "")
    .replace(/\sheight\s*=\s*["'][^"']*["']/i, "")
    .replace(/<svg\b/i, `<svg width="${w}" height="${h}"`);
  return text.replace(tag, newTag);
}

/** Lädt eine Bild-URL über onload/onerror (toleranter als `decode()`). */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(t("Bild konnte nicht geladen werden")));
    img.src = src;
  });
}

/** Rastert ein geladenes Bild auf ANALYSIS_WIDTH mit weißem Grund. */
function rasterize(img: HTMLImageElement): HTMLCanvasElement {
  let ratio = 9 / 16;
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    ratio = img.naturalHeight / img.naturalWidth;
  } else {
    // SVG ohne intrinsische Größe: Seitenverhältnis aus der gerenderten
    // Darstellung ableiten (das On-Screen-Bild ist ja sichtbar).
    const r = img.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) ratio = r.height / r.width;
  }
  const w = ANALYSIS_WIDTH;
  const h = Math.max(1, Math.round(w * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error(t("Canvas-Kontext nicht verfügbar"));
  // Weißer Grund: Transparenz im Plan soll wie "Hintergrund" wirken,
  // sonst hängt das Ergebnis von zufälligen Alpha-Werten ab.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

/**
 * Lädt das Planbild und rastert es in ein Canvas mit ANALYSIS_WIDTH.
 *
 * Primärpfad: Bild frisch fetchen (SVG bekommt bei Bedarf Pixel-Maße
 * injiziert) und via onload rastern. Schlägt das fehl — kaputte
 * Export-SVGs, Decode-Zicken — wird das bereits auf dem Bildschirm
 * dargestellte `<img>` (`fallbackImg`) direkt rastert. Das ist vom Browser
 * garantiert schon dekodiert, deshalb der verlässliche Notnagel.
 */
export async function loadPlanBitmap(
  url: string,
  fallbackImg?: HTMLImageElement | null,
): Promise<HTMLCanvasElement> {
  try {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(t("Planbild laden fehlgeschlagen ({status})", { status: resp.status }));
    const ctype = resp.headers.get("content-type") ?? "";
    let blob = await resp.blob();

    const isSvg = ctype.includes("svg") || blob.type.includes("svg");
    if (isSvg) {
      const text = await blob.text();
      blob = new Blob([ensureSvgPixelSize(text)], { type: "image/svg+xml" });
    }

    const objUrl = URL.createObjectURL(blob);
    try {
      const img = await loadImage(objUrl);
      return rasterize(img);
    } finally {
      URL.revokeObjectURL(objUrl);
    }
  } catch (e) {
    if (fallbackImg && fallbackImg.complete && fallbackImg.currentSrc) {
      return rasterize(fallbackImg);
    }
    throw e;
  }
}

/** Erkennung am Klickpunkt (Koordinaten in % der Planfläche). */
export function detectAreaAt(canvas: HTMLCanvasElement, xPct: number, yPct: number): WandResult {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return { ok: false, reason: "no-region" };
  const data = ctx.getImageData(0, 0, w, h).data;
  const sx = Math.min(w - 1, Math.max(0, Math.round((xPct / 100) * w)));
  const sy = Math.min(h - 1, Math.max(0, Math.round((yPct / 100) * h)));
  return detectRegion(data, w, h, sx, sy);
}

// ─────────────────────────────────────────────────────────────────────────────
// Reiner Kern (DOM-frei)
// ─────────────────────────────────────────────────────────────────────────────

export function detectRegion(
  rgba: Uint8ClampedArray | Uint8Array,
  w: number,
  h: number,
  seedX: number,
  seedY: number,
): WandResult {
  const n = w * h;
  const seedIdx = seedY * w + seedX;
  const sr = rgba[seedIdx * 4];
  const sg = rgba[seedIdx * 4 + 1];
  const sb = rgba[seedIdx * 4 + 2];

  // ── Versuch A: "Malbuch" — alles ist Fläche außer Konturlinien ──────
  // Barriere = deutlich andere Helligkeit als der Klickpunkt. Kein
  // Schließen: die Umrandung MUSS dicht bleiben, sonst leckt der Fill
  // bei "weiße Maschine auf weißem Boden" nach draußen.
  const seedLum = 0.299 * sr + 0.587 * sg + 0.114 * sb;
  const barrierMask = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const lum = 0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2];
    if (Math.abs(lum - seedLum) <= LUMA_BARRIER_DELTA) barrierMask[i] = 1;
  }
  const attemptA = fillAndTrace(barrierMask, w, h, seedIdx);
  if (attemptA.ok) return attemptA;

  // ── Versuch B: Farbähnlichkeit + Schließen ───────────────────────────
  // Für gemusterte Flächen (Schraffur, Streifen), die Versuch A in
  // Mini-Zellen zerhackt: dünne Musterlinien werden weggeschlossen.
  const tol2 = COLOR_TOLERANCE * COLOR_TOLERANCE;
  const mask: Uint8Array = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const dr = rgba[i * 4] - sr;
    const dg = rgba[i * 4 + 1] - sg;
    const db = rgba[i * 4 + 2] - sb;
    if (dr * dr + dg * dg + db * db <= tol2) mask[i] = 1;
  }
  let closed: Uint8Array = mask;
  for (let i = 0; i < CLOSE_RADIUS; i++) closed = dilate(closed, w, h);
  for (let i = 0; i < CLOSE_RADIUS; i++) closed = erode(closed, w, h);
  // Erosion kann den Seed selbst wegnehmen (Klick auf schmalen Bereich) —
  // dann lieber auf der rohen Maske arbeiten als gar nicht.
  if (!closed[seedIdx]) closed = mask;

  const attemptB = fillAndTrace(closed, w, h, seedIdx);
  if (attemptB.ok) return attemptB;

  // Beide fehlgeschlagen → Versuch A ist die aussagekräftigere Diagnose.
  return attemptA;
}

/** Floodfill auf `mask` + Größen-Guards + Kontur + Vereinfachung. */
function fillAndTrace(mask: Uint8Array, w: number, h: number, seedIdx: number): WandResult {
  const n = w * h;
  const region = new Uint8Array(n);
  let area = 0;
  const stack: number[] = [seedIdx];
  region[seedIdx] = 1;
  while (stack.length > 0) {
    const idx = stack.pop()!;
    area++;
    const x = idx % w;
    const y = (idx - x) / w;
    if (x > 0 && mask[idx - 1] && !region[idx - 1]) { region[idx - 1] = 1; stack.push(idx - 1); }
    if (x < w - 1 && mask[idx + 1] && !region[idx + 1]) { region[idx + 1] = 1; stack.push(idx + 1); }
    if (y > 0 && mask[idx - w] && !region[idx - w]) { region[idx - w] = 1; stack.push(idx - w); }
    if (y < h - 1 && mask[idx + w] && !region[idx + w]) { region[idx + w] = 1; stack.push(idx + w); }
  }

  const areaFrac = area / n;
  if (areaFrac > MAX_AREA_FRAC) return { ok: false, reason: "too-large" };
  if (areaFrac < MIN_AREA_FRAC) return { ok: false, reason: "too-small" };

  const contour = traceBoundary(region, w, h);
  if (!contour || contour.length < 3) return { ok: false, reason: "no-region" };

  let eps = Math.max(2, w * 0.004);
  let simplified = simplifyClosed(contour, eps);
  while (simplified.length > MAX_POINTS) {
    eps *= 1.5;
    simplified = simplifyClosed(contour, eps);
  }
  if (simplified.length < 3) return { ok: false, reason: "too-small" };

  const points = simplified.map(
    ([x, y]) => [round2((x / w) * 100), round2((y / h) * 100)] as [number, number],
  );
  return { ok: true, points, areaPct: round2(areaFrac * 100) };
}

// 3×3-Dilation: Pixel wird 1, wenn irgendein Nachbar 1 ist.
function dilate(mask: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x;
      if (mask[idx]) { out[idx] = 1; continue; }
      let hit = 0;
      for (let dy = -1; dy <= 1 && !hit; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= w) continue;
          if (mask[yy * w + xx]) { hit = 1; break; }
        }
      }
      out[idx] = hit;
    }
  }
  return out;
}

// 3×3-Erosion: Pixel bleibt 1 nur, wenn alle Nachbarn 1 sind (Rand zählt als 0).
function erode(mask: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!mask[y * w + x]) continue;
      let keep = 1;
      for (let dy = -1; dy <= 1 && keep; dy++) {
        const yy = y + dy;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (yy < 0 || yy >= h || xx < 0 || xx >= w || !mask[yy * w + xx]) { keep = 0; break; }
        }
      }
      out[y * w + x] = keep;
    }
  }
  return out;
}

/**
 * Außenkontur der Region als Kantenlauf entlang der Pixelkanten
 * (Rechte-Hand-Regel: Region bleibt rechts vom Läufer). Liefert die
 * Eckpunkte auf dem (w+1)×(h+1)-Gitter der Pixel-Ecken.
 */
function traceBoundary(region: Uint8Array, w: number, h: number): [number, number][] | null {
  // Startpixel: oberster, dann linkester Regionspixel
  let start = -1;
  for (let i = 0; i < w * h; i++) {
    if (region[i]) { start = i; break; }
  }
  if (start < 0) return null;
  const px = start % w;
  const py = (start - px) / w;

  const inRegion = (x: number, y: number): boolean =>
    x >= 0 && x < w && y >= 0 && y < h && region[y * w + x] === 1;

  // Richtungen: 0=O, 1=S, 2=W, 3=N (Uhrzeigersinn, y nach unten)
  const DX = [1, 0, -1, 0];
  const DY = [0, 1, 0, -1];
  // Zellen links/rechts der Kante von Ecke (cx,cy) in Richtung d
  const cellLeft = (cx: number, cy: number, d: number): boolean => {
    switch (d) {
      case 0: return inRegion(cx, cy - 1);
      case 1: return inRegion(cx, cy);
      case 2: return inRegion(cx - 1, cy);
      default: return inRegion(cx - 1, cy - 1);
    }
  };
  const cellRight = (cx: number, cy: number, d: number): boolean => {
    switch (d) {
      case 0: return inRegion(cx, cy);
      case 1: return inRegion(cx - 1, cy);
      case 2: return inRegion(cx - 1, cy - 1);
      default: return inRegion(cx, cy - 1);
    }
  };

  const startCx = px;
  const startCy = py;
  const startDir = 0; // Startpixel ist oberster-linker → Oberkante nach Osten
  const points: [number, number][] = [];
  let cx = startCx;
  let cy = startCy;
  let d = startDir;
  const maxSteps = 4 * (w * h + w + h);

  for (let step = 0; step < maxSteps; step++) {
    points.push([cx, cy]);
    cx += DX[d];
    cy += DY[d];
    // Richtungswahl an der neuen Ecke: Region rechts halten
    if (!cellRight(cx, cy, d)) {
      d = (d + 1) % 4; // rechts abbiegen
    } else if (cellLeft(cx, cy, d)) {
      d = (d + 3) % 4; // links abbiegen
    }
    if (cx === startCx && cy === startCy && d === startDir) {
      return points;
    }
  }
  return null; // Sicherheitsnetz — sollte nie erreicht werden
}

/** Ramer–Douglas–Peucker für geschlossene Konturen. */
function simplifyClosed(points: [number, number][], eps: number): [number, number][] {
  if (points.length < 4) return points.slice();
  // Geschlossene Kontur: ersten Punkt ans Ende duplizieren, offen vereinfachen
  const open = [...points, points[0]];
  const simplified = rdp(open, eps);
  simplified.pop();
  return simplified;
}

function rdp(points: [number, number][], eps: number): [number, number][] {
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [a, b] = stack.pop()!;
    let maxD = 0;
    let maxI = -1;
    for (let i = a + 1; i < b; i++) {
      const dist = perpDist(points[i], points[a], points[b]);
      if (dist > maxD) { maxD = dist; maxI = i; }
    }
    if (maxD > eps && maxI > 0) {
      keep[maxI] = 1;
      stack.push([a, maxI], [maxI, b]);
    }
  }
  return points.filter((_, i) => keep[i] === 1);
}

function perpDist(p: [number, number], a: [number, number], b: [number, number]): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2;
  const cx = a[0] + t * dx;
  const cy = a[1] + t * dy;
  return Math.hypot(p[0] - cx, p[1] - cy);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
